import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { Location } from '@freelancer/datastore/collections';
import { DurationFormat } from '@freelancer/pipes';
import { ModalService } from '@freelancer/ui';
import { FlagSize } from '@freelancer/ui/flag';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import {
  LinkColor,
  LinkHoverColor,
  LinkUnderline,
  LinkWeight,
} from '@freelancer/ui/link';
import { ModalSize } from '@freelancer/ui/modal';
import { PictureObjectFit } from '@freelancer/ui/picture';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import { InvitationTypeApi } from 'api-typings/users/users';

@Component({
  selector: 'fl-experience-details-pattern',
  template: `
    <div class="Container">
      <div class="HeaderContainer">
        <fl-text
          *ngIf="subtitle"
          [flMarginBottom]="'xxxsmall'"
          [flMarginRight]="'xxxsmall'"
          [size]="size"
          [sizeDesktop]="sizeDesktop"
          [sizeDesktopXXLarge]="sizeDesktopXXLarge"
          [weight]="FontWeight.MEDIUM"
        >
          {{ subtitle }}
        </fl-text>
        <fl-icon
          *ngIf="canEdit"
          class="Icon"
          [name]="'ui-edit-outline'"
          [color]="IconColor.INHERIT"
          [hoverAnimation]="'highlight'"
          [size]="IconSize.SMALL"
          [clickable]="true"
          (click)="triggerEdit()"
        ></fl-icon>
      </div>
      <div
        *ngIf="startDate"
        class="DateRoleContainer"
        [flMarginBottom]="'xxsmall'"
      >
        <fl-text
          [flMarginBottom]="'xxxsmall'"
          [flMarginRight]="'xxsmall'"
          [size]="size"
          [sizeDesktop]="sizeDesktop"
          [sizeDesktopXXLarge]="sizeDesktopXXLarge"
        >
          {{ startDate | date: 'MMM, yyyy' }} -

          <ng-container *ngIf="endDate || endDate !== undefined; else present">
            {{ endDate | date: 'MMM, yyyy' }}
          </ng-container>

          <ng-template #present>
            <ng-container i18n="Present"> Present </ng-container>
          </ng-template>
        </fl-text>

        <fl-text
          [flMarginRight]="'xxsmall'"
          [size]="size"
          [sizeDesktop]="sizeDesktop"
          [sizeDesktopXXLarge]="sizeDesktopXXLarge"
        >
          {{ '•' }}
        </fl-text>
        <fl-text
          [flMarginBottom]="'xxxsmall'"
          [flMarginRight]="'xxsmall'"
          [size]="size"
          [sizeDesktop]="sizeDesktop"
          [sizeDesktopXXLarge]="sizeDesktopXXLarge"
        >
          {{ duration | duration: DurationFormat.LONG : startDate | flAsync }}
        </fl-text>
      </div>
      <fl-text
        class="Description"
        [size]="size"
        [sizeDesktop]="sizeDesktop"
        [sizeDesktopXXLarge]="sizeDesktopXXLarge"
        >{{ description }}
      </fl-text>
    </div>
    <div
      *ngIf="location"
      class="Location"
    >
      <fl-flag
        [countryCode]="location.country?.code"
        [flMarginRight]="'xxsmall'"
        [sizeDesktop]="FlagSize.MID"
      ></fl-flag>
      <fl-text
        [flMarginRight]="'xxxsmall'"
        [size]="size"
        [sizeDesktop]="sizeDesktop"
        [sizeDesktopXXLarge]="sizeDesktopXXLarge"
      >
        {{ location.city ? location.city + ',' : '' }}
        {{ location.country?.name }}
      </fl-text>
    </div>
    <fl-link
      *ngIf="isViewerProfileOwner && showExternalReferenceRequestButton"
      class="Reference"
      i18n="Request a reference for an experience"
      flTrackingSection="Experience"
      flTrackingLabel="RequestReference"
      [color]="LinkColor.FOREGROUND"
      [display]="'block'"
      [size]="sizeDesktop ?? TextSize.SMALL"
      [sizeDesktopXXLarge]="sizeDesktopXXLarge ?? TextSize.MID"
      [underline]="LinkUnderline.ALWAYS"
      [weight]="LinkWeight.MEDIUM"
      (click)="handleRequestReferenceClick()"
    >
      <fl-icon
        [name]="'ui-add-note'"
        [useIconFont]="true"
        [fillIconFont]="false"
        [flMarginRight]="'xxsmall'"
        [size]="IconSize.SMALL"
        [sizeDesktopXXLarge]="IconSize.MID"
      >
      </fl-icon
      >Request a reference</fl-link
    >

    <ng-content> </ng-content>
  `,
  styleUrls: ['./experience-details-pattern.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExperienceDetailsPatternComponent implements OnInit {
  DurationFormat = DurationFormat;
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  LinkUnderline = LinkUnderline;
  LinkWeight = LinkWeight;
  FlagSize = FlagSize;
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  PictureObjectFit = PictureObjectFit;
  TextSize = TextSize;

  @Input() subtitle?: string;
  @Input() description?: string;
  @Input() startDate?: number;
  @Input() endDate?: number;
  @Input() duration?: number;
  @Input() location?: Location;
  @Input() canEdit: boolean;
  @Input() size = TextSize.SMALL;
  @Input() sizeDesktop?: TextSize;
  @Input() sizeDesktopXXLarge?: TextSize;
  @Input() isViewerProfileOwner?: boolean;
  @Input() experienceOwnerId: number;
  @Input() showExternalReferenceRequestButton: boolean;
  @Input() hasReferences: boolean;

  @Output() clickEdit = new EventEmitter<void>();

  constructor(private modalService: ModalService) {}

  ngOnInit(): void {
    if (this.endDate === undefined && this.startDate !== undefined) {
      this.duration = Date.now() - this.startDate;
    }
  }

  triggerEdit(): void {
    this.clickEdit.emit();
  }

  handleRequestReferenceClick(): void {
    this.modalService.open('CreateInvitationModal', {
      inputs: {
        titleText: $localize`Request a reference`,
        description: $localize`Invite your past employers to write a message about working with you.`,
        errorMessage: $localize`Failed to invite user to write a reference.`,
        contextId: this.experienceOwnerId,
        invitationType: InvitationTypeApi.EXTERNAL_REFERENCE,
        enableCustomInviteMessage: true,
        disableLinkSharing: true,
        enableInvitationListAlwaysExpanded: true,
        maxInvites: 5,
        disableCloseOnInvite: true,
        inputPlaceholder: $localize`Enter email addresses`,
      },
      size: ModalSize.SMALL,
      mobileFullscreen: true,
      customViewHeader: true,
    });
  }
}
